import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { 
  BookOpenIcon, 
  ArrowLeftIcon,
  ClockIcon,
  DocumentTextIcon,
  VideoCameraIcon,
  CodeBracketIcon,
  ChatBubbleLeftRightIcon,
  UserIcon,
  ArrowRightOnRectangleIcon,
  AcademicCapIcon
} from '@heroicons/react/24/outline';
import { 
  PlayIcon as PlayIconSolid,
  CheckCircleIcon as CheckCircleIconSolid 
} from '@heroicons/react/24/solid';
import apiService from '../services/api';
import LoadingSpinner from './LoadingSpinner';
import { useAuth } from '../contexts/AuthContext';

const CourseDetails = ({ onLogout }) => {
  const { courseId } = useParams();
  const { userProfile } = useAuth();
  const studentId = localStorage.getItem('studentId');
  const [activeLesson, setActiveLesson] = useState(0);
  const [showAITutor, setShowAITutor] = useState(false);
  const [course, setCourse] = useState(null);
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Load course data from API
  useEffect(() => {
    const loadCourseData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Check if user is authenticated
        const token = localStorage.getItem('authToken');
        if (!token) {
          setError('Please log in to access course materials.');
          setLoading(false);
          return;
        }

        // Check if student is enrolled in this course (best-effort; do not block if endpoint unavailable)
        try {
          const enrollmentStatus = await apiService.getEnrollmentStatus(courseId);
          if (enrollmentStatus && enrollmentStatus.isEnrolled === false) {
            setError('You are not enrolled in this course. Please enroll to access course materials.');
            setLoading(false);
            return;
          }
        } catch (enrollErr) {
          // If the enrollment-status endpoint is missing or fails, continue gracefully
          console.warn('Enrollment status check failed; proceeding as enrolled:', enrollErr);
        }

        // Load course details
        const courseData = await apiService.getCourseDetails(courseId);
        
        // Load course modules
        const modulesData = await apiService.getCourseModules(courseId);

        // Load materials for each module and keep only modules that actually have materials
        let modulesWithMaterials = (await Promise.all(
          modulesData.map(async (module) => {
            try {
              const materials = await apiService.getModuleMaterials(module.id);
              return { ...module, materials };
            } catch (err) {
              console.warn(`Failed to load materials for module ${module.id}:`, err);
              return { ...module, materials: [] };
            }
          })
        )).filter((m) => Array.isArray(m.materials) && m.materials.length > 0);

        // Fallback: if no module returned any materials (e.g., private-only with access issue),
        // try public materials grouped by module for the course
        if (modulesWithMaterials.length === 0) {
          const publicMaterials = await apiService.getPublicMaterialsByCourse(courseId);
          if (Array.isArray(publicMaterials) && publicMaterials.length > 0) {
            const byModule = publicMaterials.reduce((acc, mat) => {
              const modId = mat.module;
              if (!acc[modId]) acc[modId] = [];
              acc[modId].push(mat);
              return acc;
            }, {});
            modulesWithMaterials = modulesData
              .map((module) => ({ ...module, materials: byModule[module.id] || [] }))
              .filter((m) => m.materials.length > 0);
          }
        }

        // Load student's completed lessons (when progress tracking is implemented)
        let studentCompletedLessons = [];
        try {
          studentCompletedLessons = await apiService.getStudentCompletedLessons(courseId);
        } catch (err) {
          console.warn('Failed to load student progress:', err);
        }

        // Load course progress summary
        let courseProgressSummary = null;
        try {
          courseProgressSummary = await apiService.getCourseProgressSummary(courseId);
        } catch (err) {
          console.warn('Failed to load course progress summary:', err);
        }

        // Transform course data to match frontend format
        const getCurrencySymbol = (currency) => {
          switch ((currency || '').toUpperCase()) {
            case 'NGN': return '₦';
            case 'USD': return '$';
            case 'GBP': return '£';
            case 'EUR': return '€';
            default: return '';
          }
        };
        const formattedPrice = (courseData.cost_amount && courseData.cost_currency)
          ? `${getCurrencySymbol(courseData.cost_currency)}${Number(courseData.cost_amount).toLocaleString()}`
          : null;
        
        // Ensure courseData has required fields with fallbacks
        const courseName = courseData.name || courseData.title || 'Course';
        const courseDescription = courseData.description || 'This comprehensive course will help you master new skills.';
        const durationMonths = courseData.duration_months || 3; // Default to 3 months if not specified
        
        const transformedCourse = {
          id: courseData.id,
          title: courseName,
          description: courseDescription,
          writeUp: courseData.write_up || `Welcome to ${courseName}!\n\nThis comprehensive course will take you from beginner to advanced. Here are some important points to remember:\n\n📚 Course Structure:\n• Structured learning modules\n• Hands-on practical exercises\n• Real-world projects\n• Industry best practices\n\n💡 Tips for Success:\n• Practice regularly\n• Build projects alongside lessons\n• Join our community discussions\n• Don't hesitate to ask questions\n\n🛠️ Required Tools:\n• Computer with internet connection\n• Dedication and commitment\n• Willingness to learn\n\nRemember: The key to mastering any skill is consistent practice and building real projects!`,
          pdfFile: courseData.pdf_file || null,
          instructor: courseData.instructor_name || null, // Will be updated when we have teacher data
          rating: 4.8, // Placeholder - would come from reviews/ratings
          students: courseData.total_students_enrolled || 0, // Use real student count
          duration: `${durationMonths} months`,
          level: "All Levels", // Placeholder - would come from course difficulty
          progress: courseProgressSummary?.progress_percentage || 0, // Use real progress data
          totalMaterials: courseProgressSummary?.total_materials || 0,
          completedMaterials: courseProgressSummary?.completed_materials || 0,
          timeSpent: courseProgressSummary?.formatted_time_spent || "0m",
          image: "🎓",
          color: "from-blue-500 to-purple-600",
          price: formattedPrice || 'Contact for price',
        };

        // Transform modules data to match frontend format with real materials only
        const transformedModules = modulesWithMaterials.length > 0 ? modulesWithMaterials.map((module, moduleIndex) => {
          // Convert materials to lessons format
          const lessons = module.materials && module.materials.length > 0 
            ? module.materials.map((material, materialIndex) => ({
                id: material.id,
                title: material.title,
                type: material.material_type || 'document',
                duration: material.file_size ? `${Math.ceil(material.file_size / 1024 / 1024)} min` : "30 min", // Estimate based on file size
                completed: studentCompletedLessons.includes(material.id), // Use real progress data
                notes: material.notes || `📝 ${material.title}

Key Topics Covered:
• Core concepts and fundamentals
• Practical applications
• Best practices
• Real-world examples

🔧 Practice Exercises:
• Interactive coding challenges
• Project-based learning
• Hands-on activities

💡 Pro Tips:
• Take notes while learning
• Practice regularly
• Apply concepts in real projects
• Don't hesitate to ask questions

🔗 Additional Resources:
• Documentation and references
• Community discussions
• Extra practice materials`,
                material: material // Keep reference to original material data
              }))
            : [];

          return {
            id: module.id,
            title: module.name,
            description: module.description,
            teacher: module.teacher_name || "Not Assigned",
            timeSlot: module.time_slot,
            lessons: lessons
          };
        }) : [];

        // Calculate overall progress based on completed lessons
        const allLessons = transformedModules.flatMap(module => module.lessons);
        const completedLessons = allLessons.filter(lesson => lesson.completed).length;
        const overallProgress = allLessons.length > 0 ? Math.round((completedLessons / allLessons.length) * 100) : 0;

        // Update course with real progress
        transformedCourse.progress = overallProgress;

        setCourse(transformedCourse);
        setModules(transformedModules);
        // Ensure first lesson is selected if available
        const firstLesson = (transformedModules.flatMap(m => m.lessons))[0];
        if (firstLesson) {
          setActiveLesson(0);
        }

      } catch (err) {
        console.error('Failed to load course data:', err);
        
        // Provide more specific error messages
        let errorMessage = 'Failed to load course details. ';
        if (err.message.includes('Failed to fetch') || err.message.includes('NetworkError')) {
          errorMessage += 'Please make sure the backend server is running on http://127.0.0.1:8000';
        } else if (err.message.includes('404')) {
          errorMessage += 'This course may not exist or API endpoints may not be available yet.';
        } else {
          errorMessage += 'Please try again.';
        }
        
        setError(errorMessage);
        
        // Set fallback data
        const fallbackCourse = {
          id: courseId,
          title: "Course Not Found",
          description: "This course is currently unavailable.",
          writeUp: "Please check back later or contact support for assistance.",
          instructor: null,
          duration: "TBD",
          level: "All Levels",
          progress: 0,
          image: "📚",
          color: "from-gray-500 to-gray-600"
        };
        
        setCourse(fallbackCourse);
        setModules([]);
      } finally {
        setLoading(false);
      }
    };

    if (courseId) {
      loadCourseData();
    }
  }, [courseId]);

  // Create flattened lessons array
  const allLessons = modules.flatMap(module => 
    module.lessons.map(lesson => ({ ...lesson, moduleTitle: module.title }))
  );

  const currentLesson = allLessons[activeLesson] || { 
    title: "No lesson selected", 
    moduleTitle: "Course Overview",
    type: "video",
    duration: "0 min",
    notes: ""
  };

  // Handle lesson completion
  const handleLessonComplete = async (lessonId) => {
    try {
      // Find the lesson to get material info
      const lesson = modules.flatMap(module => module.lessons).find(l => l.id === lessonId);
      if (!lesson) {
        console.error('Lesson not found:', lessonId);
        return;
      }

      // Update progress in backend
      if (lesson.material) {
        await apiService.updateLessonProgress(lesson.material.id, true);
      }
      
      // Update local state
      const updatedModules = modules.map(module => ({
        ...module,
        lessons: module.lessons.map(lesson => 
          lesson.id === lessonId ? { ...lesson, completed: true } : lesson
        )
      }));
      
      setModules(updatedModules);
      
      // Recalculate overall progress
      const allLessons = updatedModules.flatMap(module => module.lessons);
      const completedLessons = allLessons.filter(lesson => lesson.completed).length;
      const overallProgress = allLessons.length > 0 ? Math.round((completedLessons / allLessons.length) * 100) : 0;
      
      setCourse(prev => ({ 
        ...prev, 
        progress: overallProgress,
        completedMaterials: prev.completedMaterials + 1
      }));
      
    } catch (error) {
      console.error('Failed to update lesson progress:', error);
    }
  };

  const getLessonIcon = (type) => {
    switch (type) {
      case 'video': return VideoCameraIcon;
      case 'interactive': return CodeBracketIcon;
      case 'reading': return DocumentTextIcon;
      case 'coding': return CodeBracketIcon;
      case 'assignment': return DocumentTextIcon;
      case 'presentation': return DocumentTextIcon;
      case 'resource': return DocumentTextIcon;
      default: return BookOpenIcon;
    }
  };

  const LessonItem = ({ lesson, index, isActive, onClick }) => {
    const Icon = getLessonIcon(lesson.type);
    
    return (
      <div 
        className={`flex items-center p-3 rounded-xl cursor-pointer transition-all duration-200 ${
          isActive 
            ? 'bg-primary-50 border-2 border-primary-200' 
            : 'hover:bg-gray-50 border-2 border-transparent'
        }`}
        onClick={() => onClick(index)}
      >
        <div className="flex-shrink-0 mr-3">
          {lesson.completed ? (
            <CheckCircleIconSolid className="w-6 h-6 text-green-500" />
          ) : (
            <div className={`w-6 h-6 rounded-full border-2 ${
              isActive ? 'border-primary-500 bg-primary-100' : 'border-gray-300'
            }`}></div>
          )}
        </div>
        
        <div className="flex-grow">
          <div className="flex items-center justify-between mb-1">
            <h4 className={`font-medium text-sm ${
              isActive ? 'text-primary-700' : 'text-gray-900'
            }`}>
              {lesson.title}
            </h4>
            <Icon className="w-4 h-4 text-gray-400" />
          </div>
          <div className="flex items-center text-xs text-gray-500">
            <ClockIcon className="w-3 h-3 mr-1" />
            {lesson.duration}
          </div>
        </div>
      </div>
    );
  };

  // Course Live Sessions Component
  const CourseLiveSessions = ({ courseId }) => {
    const [liveSessions, setLiveSessions] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
      const fetchCourseLiveSessions = async () => {
        try {
          // Filter live sessions by course ID
          const response = await apiService.getMyLiveSessions();
          const courseSessions = Array.isArray(response) 
            ? response.filter(session => session.course === courseId)
            : [];
          setLiveSessions(courseSessions);
        } catch (error) {
          console.error('Failed to fetch course live sessions:', error);
        } finally {
          setLoading(false);
        }
      };

      fetchCourseLiveSessions();
    }, [courseId]);

    if (loading) {
      return (
        <div className="card">
          <div className="animate-pulse">
            <div className="h-6 bg-gray-200 rounded mb-4"></div>
            <div className="space-y-3">
              <div className="h-4 bg-gray-200 rounded"></div>
              <div className="h-4 bg-gray-200 rounded"></div>
            </div>
          </div>
        </div>
      );
    }

    if (liveSessions.length === 0) {
      return null; // Don't show section if no live sessions
    }

    const upcomingSessions = liveSessions.filter(session => 
      session.status === 'scheduled' || session.status === 'draft'
    );
    const liveNowSessions = liveSessions.filter(session => 
      session.status === 'live'
    );

    return (
      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-gray-900 flex items-center">
            <VideoCameraIcon className="w-5 h-5 mr-2 text-blue-600" />
            Live Sessions
          </h3>
          <Link 
            to="/live-sessions"
            className="text-sm text-blue-600 hover:text-blue-700 font-medium"
          >
            View All
          </Link>
        </div>

        <div className="space-y-4">
          {/* Live Now Sessions */}
          {liveNowSessions.length > 0 && (
            <div className="p-4 bg-green-50 rounded-lg border border-green-200">
              <h4 className="text-sm font-semibold text-green-800 mb-3 flex items-center">
                <div className="w-2 h-2 bg-green-600 rounded-full mr-2 animate-pulse"></div>
                Live Now
              </h4>
              {liveNowSessions.map((session) => (
                <div key={session.id} className="flex items-center justify-between p-3 bg-white rounded-lg border border-green-200">
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-900">{session.title}</p>
                    <p className="text-xs text-gray-600">{session.description}</p>
                  </div>
                  <Link 
                    to="/live-sessions"
                    className="text-xs text-green-600 hover:text-green-700 font-medium bg-green-100 px-3 py-1 rounded-full"
                  >
                    Join Now
                  </Link>
                </div>
              ))}
            </div>
          )}

          {/* Upcoming Sessions */}
          {upcomingSessions.length > 0 && (
            <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
              <h4 className="text-sm font-semibold text-blue-800 mb-3">Upcoming Sessions</h4>
              {upcomingSessions.map((session) => (
                <div key={session.id} className="flex items-center justify-between p-3 bg-white rounded-lg border border-blue-200">
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-900">{session.title}</p>
                    <p className="text-xs text-gray-600">
                      {new Date(session.scheduled_time).toLocaleDateString()} at{' '}
                      {new Date(session.scheduled_time).toLocaleTimeString()}
                    </p>
                  </div>
                  <span className="text-xs text-blue-600 font-medium">
                    {Math.max(0, Math.floor((new Date(session.scheduled_time) - new Date()) / (1000 * 60)))} min
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center">
        <LoadingSpinner size="large" message="Loading course content..." />
      </div>
    );
  }

  if (error || !course) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-4">
          <div className="text-red-600 text-6xl mb-4">⚠️</div>
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Course Not Available</h2>
          <p className="text-gray-600 mb-6">{error || 'This course could not be loaded.'}</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/dashboard" className="btn-primary">
              Back to Dashboard
            </Link>
            <button 
              onClick={() => window.location.reload()} 
              className="btn-secondary"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      {/* Header */}
      <header className="glass-effect border-b border-white/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center">
              <Link 
                to="/dashboard" 
                className="mr-4 p-2 text-gray-600 hover:text-primary-600 transition-colors rounded-lg hover:bg-white/50"
              >
                <ArrowLeftIcon className="w-5 h-5" />
              </Link>
              <img 
                src="/images/aitech-dark.png"
                alt="Aitech Logo" 
                className="w-24 h-auto mr-3 object-contain"
              />
              <div>
                <h1 className="text-lg font-bold text-gray-900">{course.title}</h1>
                {course.instructor && (
                  <p className="text-sm text-gray-500">{course.instructor}</p>
                )}
              </div>
            </div>

            <div className="flex items-center space-x-4">
              <button 
                onClick={() => setShowAITutor(!showAITutor)}
                className="relative p-2 text-gray-600 hover:text-primary-600 transition-colors"
              >
                <ChatBubbleLeftRightIcon className="w-6 h-6" />
                {showAITutor && <span className="absolute -top-1 -right-1 w-3 h-3 bg-primary-500 rounded-full"></span>}
              </button>
              
              <div className="flex items-center space-x-3">
                <div className="text-right">
                  <p className="text-sm font-medium text-gray-900">
                    {(() => {
                      const profile = userProfile || {};
                      const profileData = profile.profile || profile.student_profile || {};
                      const name = (
                        profile.full_name ||
                        profileData.full_name ||
                        [profile.first_name, profile.last_name].filter(Boolean).join(' ') ||
                        [profileData.first_name, profileData.last_name].filter(Boolean).join(' ') ||
                        profile.username ||
                        'Student'
                      ).trim();
                      return name;
                    })()}
                  </p>
                  <p className="text-xs text-gray-500">Progress: {course.progress}%</p>
                </div>
                <div className="w-10 h-10 bg-gradient-to-r from-primary-500 to-primary-600 rounded-full flex items-center justify-center">
                  <UserIcon className="w-6 h-6 text-white" />
                </div>
              </div>

              <button 
                onClick={onLogout}
                className="p-2 text-gray-600 hover:text-red-600 transition-colors"
                title="Logout"
              >
                <ArrowRightOnRectangleIcon className="w-6 h-6" />
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Course Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Video Player / Content Area */}
            <div className="card p-0 overflow-hidden">
              <div className="bg-gray-900 aspect-video flex items-center justify-center relative">
                {currentLesson.material && currentLesson.material.youtube_url ? (
                  // Show YouTube video if available
                  <iframe
                    src={currentLesson.material.youtube_url.replace('watch?v=', 'embed/')}
                    title={currentLesson.title}
                    className="w-full h-full"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                ) : currentLesson.material && currentLesson.material.file ? (
                  // Show file viewer if available
                  <div className="w-full h-full flex items-center justify-center">
                    <div className="text-center text-white">
                      <DocumentTextIcon className="w-16 h-16 mx-auto mb-4 text-white/60" />
                      <h3 className="text-xl font-semibold mb-2">{currentLesson.title}</h3>
                      <p className="text-gray-300 mb-4">{currentLesson.moduleTitle}</p>
                      <a 
                        href={currentLesson.material.file}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn-primary inline-flex items-center"
                      >
                        <DocumentTextIcon className="w-4 h-4 mr-2" />
                        View Material
                      </a>
                    </div>
                  </div>
                ) : (
                  // Show placeholder for lessons without media
                  <div className="text-center text-white">
                    <div className="w-20 h-20 bg-white/20 rounded-full flex items-center justify-center mb-4 mx-auto">
                      <PlayIconSolid className="w-10 h-10 text-white ml-1" />
                    </div>
                    <h3 className="text-xl font-semibold mb-2">{currentLesson.title}</h3>
                    <p className="text-gray-300">{currentLesson.moduleTitle}</p>
                  </div>
                )}
                
                {/* Video Controls Overlay */}
                <div className="absolute bottom-4 left-4 right-4">
                  <div className="glass-effect rounded-lg p-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <button className="w-10 h-10 bg-primary-600 hover:bg-primary-700 rounded-full flex items-center justify-center transition-colors">
                          <PlayIconSolid className="w-5 h-5 text-white ml-0.5" />
                        </button>
                        <span className="text-white text-sm">0:00 / {currentLesson.duration}</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <button className="text-white hover:text-primary-300 transition-colors">
                          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" clipRule="evenodd" />
                          </svg>
                        </button>
                        <button className="text-white hover:text-primary-300 transition-colors">
                          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M3 4a1 1 0 011-1h4a1 1 0 010 2H6.414l2.293 2.293a1 1 0 01-1.414 1.414L5 6.414V8a1 1 0 01-2 0V4zm9 1a1 1 0 010-2h4a1 1 0 011 1v4a1 1 0 01-2 0V6.414l-2.293 2.293a1 1 0 11-1.414-1.414L13.586 5H12z" clipRule="evenodd" />
                          </svg>
                        </button>
                      </div>
                    </div>
                    <div className="mt-2">
                      <div className="w-full bg-gray-600 rounded-full h-1">
                        <div className="bg-primary-500 h-1 rounded-full" style={{ width: '35%' }}></div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Live Sessions Section */}
            <CourseLiveSessions courseId={courseId} />

            {/* Lesson Navigation */}
            <div className="flex items-center justify-between">
              <button 
                onClick={() => setActiveLesson(Math.max(0, activeLesson - 1))}
                disabled={activeLesson === 0}
                className={`btn-secondary flex items-center ${
                  activeLesson === 0 ? 'opacity-50 cursor-not-allowed' : ''
                }`}
              >
                <ArrowLeftIcon className="w-4 h-4 mr-2" />
                Previous
              </button>
              
              <div className="flex items-center space-x-2">
                <span className="text-sm text-gray-600">
                  Lesson {activeLesson + 1} of {allLessons.length}
                </span>
              </div>
              
              <button 
                onClick={() => setActiveLesson(Math.min(allLessons.length - 1, activeLesson + 1))}
                disabled={activeLesson === allLessons.length - 1}
                className={`btn-primary flex items-center ${
                  activeLesson === allLessons.length - 1 ? 'opacity-50 cursor-not-allowed' : ''
                }`}
              >
                Next
                <svg className="w-4 h-4 ml-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
            </div>

            {/* Lesson Description */}
            <div className="card">
              <h3 className="text-lg font-bold text-gray-900 mb-3">About This Lesson</h3>
              <p className="text-gray-600 mb-4">
                {currentLesson.material && currentLesson.material.description 
                  ? currentLesson.material.description
                  : `In this lesson, you'll learn the fundamentals of ${currentLesson.title.toLowerCase()}. 
                     We'll cover key concepts, practical examples, and hands-on exercises to reinforce your understanding.`
                }
              </p>
              {currentLesson.material && currentLesson.material.material_type && (
                <div className="mb-4 p-3 bg-blue-50 rounded-lg">
                  <div className="flex items-center">
                    <DocumentTextIcon className="w-5 h-5 text-blue-600 mr-2" />
                    <span className="text-sm font-medium text-gray-900">
                      Material Type: {currentLesson.material.material_type.charAt(0).toUpperCase() + currentLesson.material.material_type.slice(1)}
                    </span>
                  </div>
                </div>
              )}
                              <div className="flex items-center space-x-4 text-sm text-gray-500">
                  <div className="flex items-center">
                    <ClockIcon className="w-4 h-4 mr-1" />
                    Duration: {currentLesson.duration}
                  </div>
                  <div className="flex items-center">
                    <AcademicCapIcon className="w-4 h-4 mr-1" />
                    Level: {course.level}
                  </div>
                </div>
              {currentLesson.material && currentLesson.material.external_url && (
                <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <DocumentTextIcon className="w-5 h-5 text-blue-600 mr-2" />
                      <span className="text-sm font-medium text-gray-900">External Resource</span>
                    </div>
                    <a 
                      href={currentLesson.material.external_url} 
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn-secondary text-sm py-1 px-3"
                    >
                      Open Resource
                    </a>
                  </div>
                </div>
              )}
              
              {/* Lesson Completion Button */}
              <div className="mt-4 pt-4 border-t border-gray-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    {currentLesson.completed ? (
                      <CheckCircleIconSolid className="w-5 h-5 text-green-500 mr-2" />
                    ) : (
                      <div className="w-5 h-5 border-2 border-gray-300 rounded-full mr-2"></div>
                    )}
                    <span className="text-sm font-medium text-gray-900">
                      {currentLesson.completed ? 'Lesson Completed' : 'Mark as Complete'}
                    </span>
                  </div>
                  {!currentLesson.completed && (
                    <button
                      onClick={() => handleLessonComplete(currentLesson.id)}
                      className="btn-primary text-sm py-2 px-4"
                    >
                      Complete Lesson
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Video Notes Section */}
            {currentLesson.type === 'video' && currentLesson.notes && (
              <div className="card">
                <h3 className="text-lg font-bold text-gray-900 mb-3">
                  <DocumentTextIcon className="w-5 h-5 inline mr-2" />
                  Lesson Notes
                </h3>
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="prose prose-sm max-w-none text-gray-700 whitespace-pre-line">
                    {currentLesson.notes}
                  </div>
                </div>
              </div>
            )}

            {/* Course Write-up Section */}
            {course.writeUp && (
              <div className="card">
                <h3 className="text-lg font-bold text-gray-900 mb-3">
                  <BookOpenIcon className="w-5 h-5 inline mr-2" />
                  Course Notes & Resources
                </h3>
                <div className="bg-blue-50 rounded-lg p-4">
                  <div className="prose prose-sm max-w-none text-gray-700 whitespace-pre-line">
                    {course.writeUp}
                  </div>
                </div>
                {course.pdfFile && (
                  <div className="mt-4 p-3 bg-gray-100 rounded-lg flex items-center justify-between">
                    <div className="flex items-center">
                      <DocumentTextIcon className="w-5 h-5 text-red-600 mr-2" />
                      <span className="text-sm font-medium text-gray-900">Course PDF Material</span>
                    </div>
                    <a 
                      href={course.pdfFile} 
                      download
                      className="btn-secondary text-sm py-1 px-3"
                    >
                      Download PDF
                    </a>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Course Progress */}
            <div className="card">
              <h3 className="text-lg font-bold text-gray-900 mb-4">Course Progress</h3>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Overall Progress</span>
                  <span className="text-sm font-semibold text-primary-600">{course.progress}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-3">
                  <div 
                    className="bg-gradient-to-r from-primary-500 to-primary-600 h-3 rounded-full transition-all duration-300"
                    style={{ width: `${course.progress}%` }}
                  ></div>
                </div>
                <div className="flex items-center justify-between text-xs text-gray-500">
                  <span>{course.completedMaterials || allLessons.filter(l => l.completed).length} completed</span>
                  <span>{(course.totalMaterials || allLessons.length) - (course.completedMaterials || allLessons.filter(l => l.completed).length)} remaining</span>
                </div>
                {course.timeSpent && (
                  <div className="flex items-center justify-between text-xs text-gray-500 pt-2 border-t border-gray-100">
                    <span>Time Spent</span>
                    <span>{course.timeSpent}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Course Outline */}
            <div className="card">
              <h3 className="text-lg font-bold text-gray-900 mb-4">Course Outline</h3>
              <div className="space-y-4">
                {modules.map((module, moduleIndex) => (
                  <div key={module.id} className="space-y-2">
                    <div className="flex items-center justify-between">
                      <h4 className="font-semibold text-gray-800 text-sm">
                        Module {moduleIndex + 1}: {module.title}
                      </h4>
                      {module.teacher && module.teacher !== "Not Assigned" && (
                        <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                          {module.teacher}
                        </span>
                      )}
                    </div>
                    {module.description && (
                      <p className="text-xs text-gray-600 mb-2">{module.description}</p>
                    )}
                    {module.timeSlot && (
                      <p className="text-xs text-gray-500 mb-2">
                        <ClockIcon className="w-3 h-3 inline mr-1" />
                        {module.timeSlot}
                      </p>
                    )}
                    <div className="space-y-1">
                      {module.lessons.map((lesson, lessonIndex) => {
                        const globalIndex = allLessons.findIndex(l => l.id === lesson.id);
                        return (
                          <LessonItem
                            key={lesson.id}
                            lesson={lesson}
                            index={globalIndex}
                            isActive={globalIndex === activeLesson}
                            onClick={setActiveLesson}
                          />
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* AI Tutor Sidebar */}
      {showAITutor && (
        <div className="fixed right-0 top-0 h-full w-80 bg-white shadow-2xl z-50 border-l border-gray-200">
          <div className="p-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-bold text-gray-900">AI Tutor</h3>
              <button 
                onClick={() => setShowAITutor(false)}
                className="p-1 text-gray-400 hover:text-gray-600"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
            </div>
            <p className="text-sm text-gray-600 mt-1">Ask questions about the current lesson</p>
          </div>
          
          <div className="p-4 h-full flex flex-col">
            <div className="flex-1 bg-gray-50 rounded-lg p-4 mb-4 overflow-y-auto">
              <div className="text-center text-gray-500 text-sm">
                🤖 Hi! I'm your AI tutor. Ask me anything about "{currentLesson.title}" or any other topic in this course.
              </div>
            </div>
            
            <div className="flex space-x-2">
              <input 
                type="text"
                placeholder="Ask a question..."
                className="flex-1 input-field text-sm"
              />
              <button className="btn-primary text-sm px-4">
                Send
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CourseDetails; 